import type { CommitmentSyncSummary } from './useCommitments'
import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '@/lib/apiClient'
import { useApiClient } from '@/hooks/useApiClient'

export type RelationshipClasificacion = 'prospecto' | 'cliente' | 'socio' | 'institucion' | 'dormida'

export type RelationshipEstado = 'activa' | 'requiere_atencion' | 'pausada' | 'cerrada'
export type RelationshipPriority = 'p1' | 'p2' | 'p3'

export type RelationshipSignal = 'al_dia' | 'requiere_atencion' | 'pausada' | 'sin_contexto_suficiente'

export interface CommercialHealth {
  rulesVersion: string
  signal: RelationshipSignal
  lastActivity: string | null
  daysInactive: number | null
  threshold: number
  sourcesComplete: boolean
  issues: Array<{ code: string; label: string; points: number }>
}

export interface RelationshipListItem {
  id: string
  title: string
  clasificacion: RelationshipClasificacion
  estadoRelacion: RelationshipEstado
  prioridadComercial: RelationshipPriority | null
  pais: string | null
  segmento: string | null
  responsable: string | null
  responsableId?: string | null
  resumenActual: string | null
  signal: RelationshipSignal
  lastActivity: string | null
  nextAction: string | null
  health: CommercialHealth
}

export interface CreateRelationshipInput {
  title: string
  clasificacion: RelationshipClasificacion
  pais: string
  dominios?: string[]
  prioridadComercial?: RelationshipPriority
}

export type RelationshipsStatus =
  | 'loading'
  | 'ready'
  | 'not_signed_in'
  | 'not_configured'
  | 'forbidden'
  | 'unauthorized'
  | 'error'

interface RelationshipsState {
  status: RelationshipsStatus
  relationships: RelationshipListItem[]
  error: ApiError | Error | null
  reload: () => void
  create: (input: CreateRelationshipInput) => Promise<RelationshipListItem>
}

function statusFromError(err: unknown): RelationshipsStatus {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'unauthorized'
    if (err.status === 403) return 'forbidden'
    if (err.status === 503) return 'not_configured'
  }
  return 'error'
}

export interface RelationshipDetail {
  health: CommercialHealth
  organizacion: {
    id: string
    title: string
    nombreComercial: string | null
    clasificacion: RelationshipClasificacion
    estadoRelacion: RelationshipEstado
    prioridadComercial: RelationshipPriority | null
    pais: string | null
    segmento: string | null
    responsable: string | null
    responsableId: string | null
    activo: boolean
    resumenActual: string | null
    dominios: string[]
  }
  personas: Array<{ id: string; title: string; cargo: string | null; rolEnRelacion: string | null }>
  compromisosAbiertos: Array<{
    id: string
    title: string
    vencimiento: string | null
    estado: string
    synchronization?: CommitmentSyncSummary
    responsableId?: string | null
    responsable?: string | null
    personaId?: string | null
    descripcion?: string | null
  }>
  nextAction: { id: string; title: string; vencimiento: string | null; responsableId?: string | null } | null
  ultimaInteraccion: {
    id: string
    tipo: string
    title: string
    fecha: string
    detalle: string | null
    personaId: string | null
    url: string | null
  } | null
  historia: Array<{
    id: string
    tipo: string
    title: string
    fecha: string
    detalle: string | null
    personaId: string | null
    personaNombre?: string | null
    estado?: string
    synchronization?: CommitmentSyncSummary
    url: string | null
  }>
  iniciativas: unknown[]
  documentos: unknown[]
}

export interface UpdateRelationshipInput {
  estadoRelacion?: RelationshipEstado
  prioridadComercial?: RelationshipPriority | null
  resumenActual?: string | null
  responsableId?: string
  activo?: boolean
}

export type PersonaRolEnRelacion = 'decisor' | 'influenciador' | 'operativo' | 'sponsor' | 'otro'

export interface CreatePersonaInput {
  title: string
  cargo?: string | null
  email?: string | null
  telefono?: string | null
  rolEnRelacion?: PersonaRolEnRelacion | null
}

interface RelationshipDetailState {
  status: RelationshipsStatus
  detail: RelationshipDetail | null
  error: ApiError | Error | null
  reload: () => void
  update: (patch: UpdateRelationshipInput) => Promise<void>
  remove: () => Promise<void>
  createPersona: (input: CreatePersonaInput) => Promise<void>
}

export function useRelationshipDetail(id: string | undefined): RelationshipDetailState {
  const { gate, makeClient } = useApiClient()
  const [detail, setDetail] = useState<RelationshipDetail | null>(null)
  const [status, setStatus] = useState<RelationshipsStatus>('loading')
  const [error, setError] = useState<ApiError | Error | null>(null)
  const [attempt, setAttempt] = useState(0)

  const reload = useCallback(() => setAttempt((v) => v + 1), [])

  useEffect(() => {
    if (!id) return
    if (gate !== 'ready') return setStatus(gate)

    const controller = new AbortController()
    const client = makeClient()
    if (!client) return setStatus('not_signed_in')

    setStatus('loading')
    setError(null)
    client
      .get<RelationshipDetail>(`/api/relationships/${id}`, { signal: controller.signal })
      .then((response) => {
        setDetail(response)
        setStatus('ready')
      })
      .catch((err: ApiError | Error) => {
        if (controller.signal.aborted) return
        setDetail(null)
        setError(err)
        setStatus(statusFromError(err))
      })
    return () => controller.abort()
  }, [id, gate, attempt, makeClient])

  const update = useCallback(
    async (patch: UpdateRelationshipInput) => {
      const client = makeClient()
      if (!client || !id) throw new ApiError('API_UNAUTHORIZED', 'Not signed in')
      await client.patch(`/api/relationships/${id}`, patch)
      reload()
    },
    [makeClient, id, reload],
  )

  const remove = useCallback(async () => {
    const client = makeClient()
    if (!client || !id) throw new ApiError('API_UNAUTHORIZED', 'Not signed in')
    await client.delete(`/api/relationships/${id}`)
  }, [makeClient, id])

  const createPersona = useCallback(
    async (input: CreatePersonaInput) => {
      const client = makeClient()
      if (!client || !id) throw new ApiError('API_UNAUTHORIZED', 'Not signed in')
      await client.postOnce('/api/personas', { ...input, organizacionId: id })
      reload()
    },
    [makeClient, id, reload],
  )

  return { status, detail, error, reload, update, remove, createPersona }
}

export function useRelationships(): RelationshipsState {
  const { gate, makeClient } = useApiClient()
  const [relationships, setRelationships] = useState<RelationshipListItem[]>([])
  const [status, setStatus] = useState<RelationshipsStatus>('loading')
  const [error, setError] = useState<ApiError | Error | null>(null)
  const [attempt, setAttempt] = useState(0)

  const reload = useCallback(() => setAttempt((v) => v + 1), [])

  useEffect(() => {
    if (gate !== 'ready') {
      setStatus(gate)
      return
    }

    const controller = new AbortController()
    const client = makeClient()
    if (!client) {
      setStatus('not_signed_in')
      return
    }

    setStatus('loading')
    setError(null)

    client
      .get<{ relationships: RelationshipListItem[] }>('/api/relationships', { signal: controller.signal })
      .then((response) => {
        setRelationships(response.relationships)
        setStatus('ready')
      })
      .catch((err: ApiError | Error) => {
        if (controller.signal.aborted) return
        setRelationships([])
        setError(err)
        setStatus(statusFromError(err))
      })

    return () => controller.abort()
  }, [gate, attempt, makeClient])

  const create = useCallback(
    async (input: CreateRelationshipInput) => {
      const client = makeClient()
      if (!client) throw new ApiError('API_UNAUTHORIZED', 'Not signed in')
      const response = await client.post<{ relationship: RelationshipListItem }>('/api/relationships', input)
      reload()
      return response.relationship
    },
    [makeClient, reload],
  )

  return { status, relationships, error, reload, create }
}
